import {Component, HostListener, inject, OnInit, OnDestroy, viewChild, ElementRef, ErrorHandler} from '@angular/core';
import {MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions} from '@angular/material/dialog';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CommonModule, NgFor, NgIf} from '@angular/common';
import {AiSettingsInterface, InteractionShortcut, DEFAULT_TOOLS_AI_SETTINGS} from '@llm/interfaces/settings-ai';
import {ToolsAiStorageService} from '@llm/service/tools-ai-storage.service';
import {Tools_aiConsoleService} from '@llm/service/tools_ai-console.service';

import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatDividerModule} from '@angular/material/divider';
import {llmStoreService} from '@llm/service/llm_store.service';
import {environment} from '@env/environment';
import {NavigatorDeviceDetectorService} from "@llm/service/navigator_device_detector.service";
import {CustomErrorHandler} from "@llm/service/custom-error-handler";

@Component({
  selector: 'llm-settings-ai-dialog',
  standalone: true,
  templateUrl: './settings-ai.component.html',
  imports: [
    NgIf,
    NgFor,
    ReactiveFormsModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule
  ],
  styleUrls: ['./settings-ai.component.scss'],
    providers:[
        { provide: ErrorHandler, useClass: CustomErrorHandler }
    ]
})
export class LlmSettingsAi implements OnInit, OnDestroy {
  public dialogRef = inject(MatDialogRef<LlmSettingsAi>);
  private destroy$ = new Subject<void>();
  private _toolsAiStorageService = inject(ToolsAiStorageService);
  private _console = inject(Tools_aiConsoleService);
  private _store = inject(llmStoreService);
  protected _detector = inject(NavigatorDeviceDetectorService);


  // Propiedades para el estado del componente
  selectedTheme: AiSettingsInterface['theme'] = 'gradient';
  voiceInteractionEnabled = false;
  interactiveVoiceEnabled = false;
  selectedTone: AiSettingsInterface['tone'] = 'professional';
  username = '';
  interactionShortcut: InteractionShortcut = {...DEFAULT_TOOLS_AI_SETTINGS.interactionShortcut};
  isRecordingShortcut = false;
  consoleEnabled = false;
  logEnabled = false;
  errorEnabled = false;
  warningEnabled = false;
  debugEnabled = false;
  activeToggleBackgroundColor: string = '';

  /**
   * Listener global para capturar la combinación de teclas cuando el modo de grabación está activo.
   * @param {KeyboardEvent} event - El evento del teclado.
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.isRecordingShortcut) {
      event.preventDefault();
      event.stopPropagation();
      // Se o usuário pressionar "Escape", cancela a gravação.
      if (event.key === 'Escape') {
        this.isRecordingShortcut = false;
        this.dialogRef.disableClose = false; // Reabilita o fechamento do diálogo
        this._console.log('Gravação de atalho cancelada.');
        return;
      }
      if (['Alt', 'Control', 'Shift', 'Meta'].includes(event.key)) {
        return;
      }
      this.interactionShortcut = {
        key: event.key.toLowerCase(),
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
      };
      this.isRecordingShortcut = false;
      this._console.log('Novo atalho definido:', this.interactionShortcut);
    }
  }

  constructor(
    private elementRef: ElementRef
  ) {
    this._console.log('Dialogo de configraciónes iniciado')
  }

  ngOnInit(): void {
    this.applyTheme(this._store.settingsToolsAi().theme);
    this.activeToggleBackgroundColor = this.selectedTheme == 'blue' ? '#2196F3' : '#673ab7';
    this.loadSettings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga las configuraciones guardadas desde el servicio de almacenamiento.
   */
  private loadSettings(): void {
    const savedSettings = this._store.settingsToolsAi();
    if (savedSettings) {
      this._console.log('Configurações carregadas:', savedSettings);
      // Actualizamos las propiedades del componente con los valores guardados.
      this.selectedTheme = savedSettings.theme;
      this.voiceInteractionEnabled = savedSettings.voiceInteractionEnabled;
      this.interactiveVoiceEnabled = savedSettings.interactiveVoiceEnabled;
      this.selectedTone = savedSettings.tone;
      this.username = savedSettings.username;
      this.interactionShortcut = savedSettings.interactionShortcut ?? {...DEFAULT_TOOLS_AI_SETTINGS.interactionShortcut};
      this.consoleEnabled = savedSettings.consoleEnabled;
      this.logEnabled = savedSettings.logEnabled;
      this.errorEnabled = savedSettings.errorEnabled;
      this.warningEnabled = savedSettings.warningEnabled;
      this.debugEnabled = savedSettings.debugEnabled;
    } else {
     this._console.log('Nenhuma configuração salva encontrada. Usando valores padrão.');
    }
  }

  /**
   * Inicia el modo de grabación para un nuevo atajo de teclado.
   */
  startRecordingShortcut(): void {
    this.isRecordingShortcut = true;
    this.dialogRef.disableClose = true; // Desabilita o fechamento do diálogo durante a gravação
    this._console.log('Aguardando nova combinação de teclas...');
  }

  /**
   * Getter para dividir el atajo en un array de strings para mostrarlo visualmente.
   * @returns {string[]} El atajo como un array de teclas.
   */
  get shortcutKeysArray(): string[] {
    if (!this.interactionShortcut || !this.interactionShortcut.key) {
      return ['Definir Atalho'];
    }
    const parts: string[] = [];
    if (this.interactionShortcut.ctrlKey) parts.push('CTRL');
    if (this.interactionShortcut.altKey) parts.push('ALT');
    if (this.interactionShortcut.shiftKey) parts.push('SHIFT');
    parts.push(this.interactionShortcut.key.toUpperCase());
    return parts;
  }

  // Actualiza las variables CSS según el tema seleccionado
  applyTheme(theme: 'blue' | 'gradient' | 'violet'): void {

    const paletteOptions = document.querySelectorAll('.llm-settings-palette-option');
    paletteOptions.forEach(option => {
      option.classList.remove('selected');
    });
    const selectedOption = document.querySelector(`.llm-settings-palette-option[data-theme="${theme}"]`);
    if (selectedOption) {
      selectedOption.classList.add('selected');
    }
    switch (theme) {
      case 'blue':
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-background', 'var(--llm-settings-primary-color-blue)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-color-solid', 'var(--llm-settings-primary-color-blue)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-color-solid-transparent', 'rgba(33, 150, 243, 0.7)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-toggle-background', '#bbdefb');
        break;
      case 'violet':
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-background', 'var(--llm-settings-primary-color-violet)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-color-solid', 'var(--llm-settings-primary-color-violet)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-color-solid-transparent', 'rgba(103, 58, 183, 0.7)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-toggle-background', '#d1c4e9');
        break;
      case 'gradient':
      default:
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-background', 'var(--llm-settings-primary-gradient)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-color-solid', 'var(--llm-settings-primary-color-violet)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-color-solid-transparent', 'rgba(103, 58, 183, 0.7)');
        this.elementRef.nativeElement.style.setProperty('--llm-settings-active-toggle-background', '#d1c4e9');
        break;
    }
  }

  selectTheme(theme: 'blue' | 'gradient' | 'violet'): void {
    if (this.selectedTheme === theme) {
      return;
    }
    this.selectedTheme = theme;

    this.applyTheme(theme);
  }

  // Guarda las configuraciones
  saveSettings(): void {
    this._console.log('Configuraciones guardadas:', {
      theme: this.selectedTheme,
      voiceInteractionEnabled: this.voiceInteractionEnabled,
      interactiveVoiceEnabled: this.interactiveVoiceEnabled,
      tone: this.selectedTone,
      username: this.username,
      interactionShortcut: this.interactionShortcut,
      consoleEnabled: this.consoleEnabled,
      logEnabled: this.logEnabled,
      errorEnabled: this.errorEnabled,
      warningEnabled: this.warningEnabled,
      debugEnabled: this.debugEnabled,
    });
    const currentSettings: AiSettingsInterface = {
      theme: this.selectedTheme,
      voiceInteractionEnabled: this.voiceInteractionEnabled,
      interactiveVoiceEnabled: this.interactiveVoiceEnabled,
      tone: this.selectedTone,
      username: this.username,
      interactionShortcut: this.interactionShortcut,
      consoleEnabled: this.consoleEnabled,
      logEnabled: this.logEnabled,
      errorEnabled: this.errorEnabled,
      warningEnabled: this.warningEnabled,
      debugEnabled: this.debugEnabled,
    };
    this._console.log('Configurações salvas:', currentSettings);
    this._toolsAiStorageService.saveSettingsAi(currentSettings);
    this.dialogRef.close();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  protected readonly environment = environment;
}
